import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import MainLayout from "../layouts/MainLayout";
import ClientTable from "../components/ClientTable";
import ClientPagination from "../components/ClientPagination";
import useApi from "../utils/useApi";
import { useLoading } from "../context/LoadingContext";
import { Modal } from "bootstrap";
import ConfirmPasswordModal from "../components/ConfirmPasswordModal";

const BrowseClients = () => {
  const { callApi } = useApi();
  const { setLoading } = useLoading();
  const [clients, setClients] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]); // Track selected users
  const [searchQuery, setSearchQuery] = useState("");
  const [searchLetter, setSearchLetter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const clientsPerPage = parseInt(process.env.REACT_APP_CLIENT_PER_PAGE) || 100;
  const [totalClients, setTotalClients] = useState(0);
  const navigate = useNavigate();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // **🔹 Fetch Clients From API**
  const fetchClients = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        length: clientsPerPage,
        keyword: searchQuery || undefined,
        letter: searchLetter !== "All" ? searchLetter : undefined,
      };

      const response = await callApi({ endpoint: "/admin/clients", params });

      setClients(response.data.users || []);
      setTotalClients(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
      setLoading(false);
    }
  };

  // 📌 Fetch Clients when Page Loads & When Filters Change
  useEffect(() => {
    fetchClients();
  }, [currentPage, searchQuery, searchLetter]);

  // 🔹 Handle Selecting Individual Users
  const toggleUserSelection = (userId) => {
    setSelectedUsers((prevSelected) => {
      if (prevSelected.includes(userId)) {
        return prevSelected.filter((id) => id !== userId); // ✅ Deselect user
      } else {
        return [...prevSelected, userId]; // ✅ Select user
      }
    });
  };

  // 🔹 Handle "Select All"
  const toggleSelectAll = () => {
    const allSelected =
      selectedUsers.length === clients.length && clients.length > 0;
    if (allSelected) {
      setSelectedUsers([]); // ✅ Deselect all users
    } else {
      setSelectedUsers(clients.map((client) => client.user_id)); // ✅ Select only users on the current page
    }
  };

  // 🔹 Handle Search by Letter
  const actionSearchLetter = (letter) => {
    setCurrentPage(1);
    setSearchLetter(letter);
  };

  // 🔹 Handle Search by Keyword
  const actionSearchQuery = (query) => {
    setCurrentPage(1);
    setSearchQuery(query);
  };

  // **🔹 Bulk Delete Clients with SweetAlert**
  const handleBulkDelete = async () => {
    console.log(selectedUsers);
    if (selectedUsers.length === 0) {
      return Swal.fire(
        "No Users Selected",
        "Please select users to delete.",
        "warning"
      );
    }
    try {
      setLoading(true);
      await callApi({
        endpoint: "admin/clients/destroy",
        method: "DELETE",
        data: { user_ids: selectedUsers },
      });

      // Remove deleted users from state
      setClients((prevClients) =>
        prevClients.filter((client) => !selectedUsers.includes(client.id))
      );
      setSelectedUsers([]); // Clear selection
      Swal.fire(
        "Deleted!",
        `${selectedUsers.length} users have been deleted.`,
        "success"
      );
      fetchClients();
    } catch (error) {
      console.error("Error deleting users:", error);
      Swal.fire("Error", "Failed to delete users. Try again later.", "error");
    } finally {
      setLoading(false);
    }
  };

  const editClient = (userId) => {
    console.log("Editing client with ID:", userId);
    navigate(`/edit-client/${userId}`); // Redirect to an edit page
  };

  const deleteClient = async (userId) => {
    try {
      setLoading(true);
      await callApi({
        endpoint: "admin/clients/destroy",
        method: "DELETE",
        data: { user_ids: [userId] },
      });

      // Remove from state
      setClients((prevClients) =>
        prevClients.filter((client) => client.user_id !== userId)
      );
      Swal.fire("Deleted!", "Client has been deleted.", "success");
    } catch (error) {
      console.error("Error deleting client:", error);
      Swal.fire("Error", "Failed to delete client. Try again later.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="inner_full_right_part">
        <div className="row">
          <div className="col-md-12">
            <div className="global_cardbox">
              <div className="global_card_header">
                <div className="row">
                  <div className="col-md-3">
                    <div className="global_head_tag">
                      <span>
                        <i className="fa-solid fa-folder-open"></i>
                      </span>{" "}
                      Browse Clients <em>({totalClients} Clients)</em>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="heading_block">
                    <input
                        type="text"
                        name="fakeusernameremembered"
                        style={{ display: "none" }}
                        autoComplete="username"
                        tabIndex={-1}
                      />
                      <input
                        type="search"
                        placeholder="Search Clients"
                         autoComplete="abcd"
                        value={searchQuery}
                        onChange={(e) => actionSearchQuery(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="heading_block">
                      <label>Search By Letter</label>
                      <select
                        value={searchLetter}
                        onChange={(e) => actionSearchLetter(e.target.value)}
                      >
                        <option>All</option>
                        {[..."ABCDEFGHIJKLMNOPQRSTUVWXYZ"].map((letter) => (
                          <option key={letter}>{letter}</option>
                        ))}
                      </select>
                      <Link
                        className="btn btn-primary orange_btn"
                        to="/create-clients"
                      >
                        <span>
                          <i className="fa-solid fa-plus"></i>
                        </span>{" "}
                        Create Client
                      </Link>
                      {/* 🔹 Delete Button Shows Count of Selected Users */}
                      <button
                        className="btn btn-primary red_btn"
                        onClick={() => setShowConfirmModal(true)}
                        disabled={selectedUsers.length === 0}
                      >
                        <span>
                          <i className="fa-solid fa-trash-can"></i>
                        </span>
                        Delete{" "}
                        {selectedUsers.length > 0
                          ? `(${selectedUsers.length})`
                          : "Client"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="browse_client_holder">
                {/* ✅ Pass selection handlers to ClientTable */}
                <ClientTable
                  clients={clients}
                  selectedUsers={selectedUsers}
                  toggleUserSelection={toggleUserSelection}
                  toggleSelectAll={toggleSelectAll}
                  editClient={editClient}
                  deleteClient={deleteClient}
                />
                <ClientPagination
                  currentPage={currentPage}
                  totalPages={Math.ceil(totalClients / clientsPerPage)}
                  setCurrentPage={setCurrentPage}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Use ConfirmPasswordModal */}
      <ConfirmPasswordModal
        show={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onSuccess={handleBulkDelete} // ✅ Execute delete after password confirmation
      />
    </MainLayout>
  );
};

export default BrowseClients;
